import React, { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { BookOpen, Compass, GraduationCap, Award, FileCheck, CheckCircle, Star, TrendingUp, Zap, Crown } from 'lucide-react';

const ICON_MAP = {
  'book-open': BookOpen,
  'compass': Compass,
  'graduation-cap': GraduationCap,
  'award': Award,
  'file-check': FileCheck,
  'check-circle': CheckCircle,
  'star': Star,
  'trending-up': TrendingUp,
  'zap': Zap,
  'crown': Crown,
};

function ConfettiParticle({ color, delay, left, size, duration }) {
  return (
    <div
      className="absolute rounded-sm pointer-events-none"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: color,
        left: `${left}%`,
        top: '-10px',
        opacity: 0,
        animation: `confetti-fall ${duration}s ease-out ${delay}s forwards`,
      }}
    />
  );
}

function SparkleRing({ color, delay }) {
  return (
    <div
      className="absolute rounded-full pointer-events-none"
      style={{
        width: '120px',
        height: '120px',
        border: `2px solid ${color}`,
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%) scale(0)',
        opacity: 0,
        animation: `sparkle-ring 1s ease-out ${delay}s forwards`,
      }}
    />
  );
}

export default function BadgePopup({ badge, onClose }) {
  const { language, t } = useLanguage();
  const [show, setShow] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const particles = useMemo(() => {
    const colors = [badge?.color, '#FFD700', '#FF6B6B', '#4ECDC4', '#A78BFA', '#FB923C'];
    return Array.from({ length: 40 }, (_, i) => ({
      id: i,
      color: colors[i % colors.length],
      delay: Math.random() * 0.8,
      left: Math.random() * 100,
      size: Math.random() * 6 + 3,
      duration: Math.random() * 1.5 + 1.2,
    }));
  }, [badge]);

  useEffect(() => {
    const t1 = setTimeout(() => setShow(true), 50);
    const t2 = setTimeout(() => setShowContent(true), 300);
    const t3 = setTimeout(() => {
      setShow(false);
      setShowContent(false);
      setTimeout(onClose, 500);
    }, 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onClose]);

  if (!badge) return null;

  const Icon = ICON_MAP[badge.icon] || Award;
  const name = language === 'en' ? badge.name_en : badge.name_es;
  const desc = language === 'en' ? badge.description_en : badge.description_es;

  return (
    <>
      {/* Confetti keyframes */}
      <style>{`
        @keyframes confetti-fall {
          0% { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }
          50% { opacity: 1; }
          100% { opacity: 0; transform: translateY(320px) rotate(${Math.random() > 0.5 ? '' : '-'}720deg) scale(0.3); }
        }
        @keyframes sparkle-ring {
          0% { opacity: 0.8; transform: translate(-50%, -50%) scale(0); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(3); }
        }
        @keyframes badge-bounce {
          0% { transform: scale(0) rotate(-20deg); }
          50% { transform: scale(1.15) rotate(5deg); }
          70% { transform: scale(0.95) rotate(-2deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px ${badge.color}40, 0 0 60px ${badge.color}20; }
          50% { box-shadow: 0 0 40px ${badge.color}60, 0 0 100px ${badge.color}30; }
        }
        @keyframes text-reveal {
          0% { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>

      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[9998] transition-all duration-500 ${show ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
        onClick={() => { setShow(false); setShowContent(false); setTimeout(onClose, 500); }}
      />

      {/* Popup */}
      <div
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] transition-all duration-500 ${show ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
        data-testid="badge-popup"
      >
        <div
          className="relative rounded-3xl px-10 py-8 min-w-[380px] max-w-[460px] overflow-hidden"
          style={{
            background: `linear-gradient(145deg, #FFFFFF, ${badge.color}08)`,
            border: `2px solid ${badge.color}30`,
            boxShadow: `0 25px 80px -12px ${badge.color}40`,
          }}
        >
          {/* Confetti particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {particles.map(p => (
              <ConfettiParticle key={p.id} {...p} />
            ))}
          </div>

          {/* Sparkle rings */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <SparkleRing color={badge.color} delay={0.2} />
            <SparkleRing color={badge.color} delay={0.5} />
            <SparkleRing color={badge.color} delay={0.8} />
          </div>

          {/* Content */}
          <div className="relative text-center">
            {/* Label */}
            <div
              className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-[0.2em] mb-5"
              style={{
                color: badge.color,
                background: `${badge.color}15`,
                fontFamily: 'Montserrat',
                opacity: showContent ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              {t('new_badge')}
            </div>

            {/* Badge Icon */}
            <div className="flex justify-center mb-5">
              <div
                className="w-24 h-24 rounded-3xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${badge.color}, ${badge.color}BB)`,
                  animation: showContent ? 'badge-bounce 0.7s ease-out forwards, glow-pulse 2s ease-in-out 0.7s infinite' : 'none',
                  transform: showContent ? undefined : 'scale(0)',
                }}
              >
                <Icon className="w-12 h-12 text-white" strokeWidth={2} />
              </div>
            </div>

            {/* Badge Name */}
            <h3
              className="text-2xl font-black text-slate-800 mb-1"
              style={{
                fontFamily: 'Montserrat',
                animation: showContent ? 'text-reveal 0.5s ease-out 0.4s forwards' : 'none',
                opacity: showContent ? undefined : 0,
              }}
            >
              {name}
            </h3>

            {/* Description */}
            <p
              className="text-sm text-slate-500 mb-5"
              style={{
                animation: showContent ? 'text-reveal 0.5s ease-out 0.6s forwards' : 'none',
                opacity: showContent ? undefined : 0,
              }}
            >
              {desc}
            </p>

            {/* Shimmer bar */}
            <div
              className="h-1 rounded-full mx-auto mb-5"
              style={{
                width: '60%',
                background: `linear-gradient(90deg, transparent, ${badge.color}, transparent)`,
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s linear infinite',
              }}
            />

            {/* Congratulations text */}
            <p
              className="text-xs font-bold uppercase tracking-wider"
              style={{
                color: badge.color,
                fontFamily: 'Montserrat',
                animation: showContent ? 'text-reveal 0.5s ease-out 0.8s forwards' : 'none',
                opacity: showContent ? undefined : 0,
              }}
            >
              {t('congratulations')}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={() => { setShow(false); setShowContent(false); setTimeout(onClose, 500); }}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all text-lg"
            data-testid="badge-popup-close"
          >
            &times;
          </button>
        </div>
      </div>
    </>
  );
}
